import "./App.css";
import "./index.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { I18nProvider } from "@heroui/react";
import { store } from "./store/store";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import Map from "./components/map";
import EventsCalendar from "./components/calendar";
import {
    EventFiltersBar,
    type UiFilters,
} from "./components/filters/EventFiltersBar";
import { Header } from "./components/layout/Header";
import { EventDetailModal } from "./components/events/EventDetailModal";
import { useGetEventByIdQuery, useGetEventsQuery } from "./reducers/apiSlice";
import { selectEvent, clearSelectedEvent } from "./reducers/eventReducer";
import { eventFiltersToApiQuery } from "./utils/eventFilterApi";
import {
    parseEventFiltersFromSearchParams,
    serializeEventFiltersToSearchParams,
    filtersEqual,
    mergeEventFilters,
} from "./utils/eventFilterUrl";
import {
    applyEventIdToSearchParams,
    parseEventIdFromSearchParams,
} from "./utils/eventUrl";
import { filterByTimeOfDay } from "./utils/eventDisplay";
import type { EventFilters } from "./types/eventFilters";
import { defaultEventFilters } from "./types/eventFilters";
import type { Event } from "./reducers/apiTypes";

const defaultUiFilters: UiFilters = {
    timeOfDay: null,
    freeOnly: false,
};

function AppShell() {
    const dispatch = useAppDispatch();
    const selectedEvent = useAppSelector((state) => state.event.selectedEvent);
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState<"map" | "calendar">("map");
    const [uiFilters, setUiFilters] = useState<UiFilters>(defaultUiFilters);
    const [filters, setFilters] = useState<EventFilters>(() =>
        typeof window === "undefined"
            ? defaultEventFilters
            : parseEventFiltersFromSearchParams(
                  new URLSearchParams(window.location.search)
              )
    );
    const prevUrlEventIdRef = useRef<number | null>(null);

    const urlEventId = useMemo(
        () => parseEventIdFromSearchParams(searchParams),
        [searchParams]
    );
    const { data: urlEvent } = useGetEventByIdQuery(urlEventId!, {
        skip: urlEventId == null,
    });

    useEffect(() => {
        setFilters((prev) => {
            const next = parseEventFiltersFromSearchParams(searchParams);
            return filtersEqual(prev, next) ? prev : next;
        });
    }, [searchParams]);

    useEffect(() => {
        const serialized = serializeEventFiltersToSearchParams(filters);
        const next = applyEventIdToSearchParams(
            serialized,
            selectedEvent?.id ?? null
        );
        if (next.toString() === searchParams.toString()) return;
        setSearchParams(next, { replace: true });
    }, [filters, selectedEvent, searchParams, setSearchParams]);

    useEffect(() => {
        if (urlEventId === prevUrlEventIdRef.current) return;
        prevUrlEventIdRef.current = urlEventId;

        if (urlEventId == null) {
            dispatch(clearSelectedEvent());
            return;
        }
        if (selectedEvent?.id === urlEventId) return;
        if (urlEvent) dispatch(selectEvent(urlEvent));
    }, [urlEventId, urlEvent, selectedEvent, dispatch]);

    const apiQuery = useMemo(() => eventFiltersToApiQuery(filters), [filters]);
    const { data: events } = useGetEventsQuery(apiQuery);

    const displayEvents = useMemo(() => {
        const list = events ?? [];
        return filterByTimeOfDay(list, uiFilters.timeOfDay);
    }, [events, uiFilters.timeOfDay]);

    const onBarChange = useCallback((next: EventFilters) => {
        setFilters((prev) => (filtersEqual(prev, next) ? prev : next));
    }, []);

    const onBoundsChange = useCallback((bounds: EventFilters["bounds"]) => {
        setFilters((prev) => {
            const next = mergeEventFilters(prev, { bounds });
            return filtersEqual(prev, next) ? prev : next;
        });
    }, []);

    const handleSelectEvent = useCallback(
        (event: Event) => {
            dispatch(selectEvent(event));
        },
        [dispatch]
    );

    const handleCloseEvent = useCallback(() => {
        dispatch(clearSelectedEvent());
        const next = applyEventIdToSearchParams(
            serializeEventFiltersToSearchParams(filters),
            null
        );
        prevUrlEventIdRef.current = null;
        setSearchParams(next, { replace: true });
    }, [dispatch, filters, setSearchParams]);

    return (
        <div className="app-root flex h-screen max-h-screen flex-col overflow-hidden bg-background text-foreground">
            <Header />
            <div className="shrink-0">
                <EventFiltersBar
                    filters={filters}
                    uiFilters={uiFilters}
                    onChange={onBarChange}
                    onUiChange={setUiFilters}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    eventCount={displayEvents.length}
                />
            </div>
            <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {viewMode === "map" ? (
                    <Map
                        events={displayEvents}
                        boundsFilter={filters.bounds}
                        onBoundsChange={(b) => onBoundsChange(b)}
                        selectedEventId={selectedEvent?.id ?? null}
                        onSelectEvent={handleSelectEvent}
                        filters={filters}
                    />
                ) : (
                    <EventsCalendar
                        events={displayEvents}
                        onEventClick={handleSelectEvent}
                    />
                )}
            </main>
            <EventDetailModal
                event={selectedEvent}
                onClose={handleCloseEvent}
            />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <I18nProvider locale="en-US">
                <Provider store={store}>
                    <AppShell />
                </Provider>
            </I18nProvider>
        </BrowserRouter>
    );
}

export default App;
